import React, { useState, useEffect, useMemo } from 'react';
import { fetchTaskTokenUsage, fetchWorkflowTokenUsage } from '../../services/api';
import { 
    IoClose, 
    IoChevronDown, 
    IoBuildOutline, 
    IoChatbubblesOutline, 
    IoCubeOutline,
    IoArrowUpCircleOutline,
    IoArrowDownCircleOutline
} from 'react-icons/io5';
import { VscLoading } from "react-icons/vsc";

// --- HELPER FUNCTIONS ---

const formatNumber = (num) => (num || 0).toLocaleString('en-US');
const formatCost = (cost) => `$${(cost || 0).toFixed(5)}`;
const calculateCost = (tokens, costPerMillion) => (tokens / 1_000_000) * (costPerMillion || 0);

// --- REUSABLE UI COMPONENTS ---

/**
 * A redesigned, more spacious collapsible section.
 * It now shows separate Input/Output token counts instead of a total.
 */
const CollapsibleSection = ({ title, description, stats, children, initialOpen = false, level = 0 }) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const levelStyles = ['bg-2', 'bg-body'];
    const paddingLeft = `${level * 1.5}rem`;

    return (
        <div className={`${levelStyles[level % 2]} rounded-lg`}>
            <header style={{ paddingLeft }} className="flex justify-between items-center p-3 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-p-text truncate">{title}</h4>
                    {description && <p className="text-xs text-s-text mt-1 truncate">{description}</p>}
                </div>
                <div className="flex items-center gap-4 pl-4">
                    <div className="text-right">
                        <p className="font-bold text-p-text text-sm">{stats.cost}</p>
                        <div className="text-xs text-s-text flex items-center justify-end gap-3 mt-1">
                            <span className="flex items-center gap-1" title="Input Tokens"><IoArrowUpCircleOutline className="text-blue-400"/> {formatNumber(stats.inputTokens)}</span>
                            <span className="flex items-center gap-1" title="Output Tokens"><IoArrowDownCircleOutline className="text-green-400"/> {formatNumber(stats.outputTokens)}</span>
                        </div>
                    </div>
                    <IoChevronDown className={`text-s-text transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </header>
            {isOpen && <div className="pb-2" style={{ paddingLeft }}><div className="border-l-2 border-line ml-3 pl-3 space-y-2">{children}</div></div>}
        </div>
    );
};

/**
 * A clear, table-like row for the lowest level of detail (Task or Node).
 * This component is already correctly designed.
 */
const DetailRow = ({ title, id, input, output, costs }) => {
    const inputCost = calculateCost(input, costs.input);
    const outputCost = calculateCost(output, costs.output);
    const totalCost = inputCost + outputCost;

    return (
        <div className="p-3 bg-1 rounded-md border border-line">
            <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-p-text flex items-center gap-2"><IoCubeOutline /> {title}</p>
                <p className="font-mono text-xs text-s-text">ID: {id}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-2 p-2 rounded">
                    <p className="text-s-text">Input</p>
                    <p className="font-semibold text-p-text">{formatNumber(input)}</p>
                    <p className="font-mono text-blue-400">{formatCost(inputCost)}</p>
                </div>
                <div className="bg-2 p-2 rounded">
                    <p className="text-s-text">Output</p>
                    <p className="font-semibold text-p-text">{formatNumber(output)}</p>
                    <p className="font-mono text-green-400">{formatCost(outputCost)}</p>
                </div>
                <div className="bg-2 p-2 rounded">
                    <p className="text-s-text">Total Cost</p>
                    <p className="font-bold text-p-text">{formatCost(totalCost)}</p>
                </div>
            </div>
        </div>
    );
};


// --- MAIN MODAL COMPONENT ---
export const TokenUsageModal = ({ isOpen, onClose, user, userIdToken, costSettings }) => {
    const [activeTab, setActiveTab] = useState('tasks');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [taskData, setTaskData] = useState(null);
    const [workflowData, setWorkflowData] = useState(null);

    useEffect(() => {
        if (isOpen && user) {
            const fetchUsageData = async () => {
                setLoading(true);
                setError(null);
                try {
                    const [tasks, workflows] = await Promise.all([
                        fetchTaskTokenUsage(userIdToken, user.email),
                        fetchWorkflowTokenUsage(userIdToken, user.email)
                    ]);
                    setTaskData(tasks);
                    setWorkflowData(workflows);
                } catch (err) {
                    setError(err.message || 'Failed to fetch usage data.');
                } finally {
                    setLoading(false);
                }
            };
            fetchUsageData();
        }
    }, [isOpen, user, userIdToken]);
    
    const totals = useMemo(() => {
        const taskInputCost = calculateCost(taskData?.grand_total_input_tokens, costSettings.tasks.input);
        const taskOutputCost = calculateCost(taskData?.grand_total_output_tokens, costSettings.tasks.output);
        const wfInputCost = calculateCost(workflowData?.grand_total_input_tokens, costSettings.workflows.input);
        const wfOutputCost = calculateCost(workflowData?.grand_total_output_tokens, costSettings.workflows.output);
        
        return {
            tasks: taskInputCost + taskOutputCost,
            workflows: wfInputCost + wfOutputCost,
            grand: taskInputCost + taskOutputCost + wfInputCost + wfOutputCost
        };
    }, [taskData, workflowData, costSettings]);

    if (!isOpen) return null;

    const renderContent = () => {
        if (loading) return <div className="flex flex-col items-center justify-center p-10 text-s-text"><VscLoading className="animate-spin text-4xl mb-4" />Loading usage data...</div>;
        if (error) return <div className="text-center p-10 text-red-400">Error: {error}</div>;

        if (activeTab === 'tasks') {
            const projects = taskData?.projects_breakdown || [];
            if (projects.length === 0) return <div className="text-center p-10 text-s-text">No task usage data found.</div>;
            return (
                <div className="space-y-3">
                    {projects.map(proj => {
                        const projectCost = calculateCost(proj.project_total_input_tokens, costSettings.tasks.input) + calculateCost(proj.project_total_output_tokens, costSettings.tasks.output);
                        return (
                            <CollapsibleSection
                                key={proj.project_id}
                                title={proj.project_name || 'Unnamed Project'}
                                description={`Project ID: ${proj.project_id}`}
                                stats={{ 
                                    cost: formatCost(projectCost), 
                                    inputTokens: proj.project_total_input_tokens, 
                                    outputTokens: proj.project_total_output_tokens
                                }}
                                level={0}
                            >
                                <div className="space-y-2">
                                   {proj.tasks_breakdown.map(task => (
                                        <DetailRow key={task.task_id} title="Task" id={task.task_id} input={task.input_tokens} output={task.output_tokens} costs={costSettings.tasks} />
                                   ))}
                               </div>
                            </CollapsibleSection>
                        );
                    })}
                </div>
            );
        }

        if (activeTab === 'workflows') {
            const projects = workflowData?.projects_breakdown || [];
            if (projects.length === 0) return <div className="text-center p-10 text-s-text">No workflow usage data found.</div>;
            return (
                <div className="space-y-3">
                    {projects.map(proj => {
                        const projectCost = calculateCost(proj.project_total_input_tokens, costSettings.workflows.input) + calculateCost(proj.project_total_output_tokens, costSettings.workflows.output);
                        return (
                             <CollapsibleSection 
                                key={proj.project_id} 
                                title={proj.project_name} 
                                description={`Project ID: ${proj.project_id}`} 
                                stats={{ 
                                    cost: formatCost(projectCost), 
                                    inputTokens: proj.project_total_input_tokens,
                                    outputTokens: proj.project_total_output_tokens
                                }} 
                                level={0}
                            >
                               {proj.workflows.map(wf => {
                                   const wfCost = calculateCost(wf.workflow_total_input, costSettings.workflows.input) + calculateCost(wf.workflow_total_output, costSettings.workflows.output);
                                   return (
                                       <CollapsibleSection 
                                            key={wf.workflow_id} 
                                            title={wf.title} 
                                            description={wf.description} 
                                            stats={{ 
                                                cost: formatCost(wfCost), 
                                                inputTokens: wf.workflow_total_input,
                                                outputTokens: wf.workflow_total_output
                                            }} 
                                            level={1}
                                        >
                                           {wf.executions.map(exec => {
                                               const execCost = calculateCost(exec.execution_total_input, costSettings.workflows.input) + calculateCost(exec.execution_total_output, costSettings.workflows.output);
                                               return (
                                                   <CollapsibleSection 
                                                        key={exec.execution_id} 
                                                        title={`Execution`} 
                                                        description={`ID: ${exec.execution_id}`} 
                                                        stats={{ 
                                                            cost: formatCost(execCost), 
                                                            inputTokens: exec.execution_total_input,
                                                            outputTokens: exec.execution_total_output
                                                        }} 
                                                        level={2}
                                                    >
                                                        <div className="space-y-2">
                                                           {exec.nodes.map(node => (
                                                               <DetailRow key={node.node_id} title="Node" id={node.node_id} input={node.input_tokens} output={node.output_tokens} costs={costSettings.workflows} />
                                                           ))}
                                                       </div>
                                                   </CollapsibleSection>
                                               );
                                           })}
                                       </CollapsibleSection>
                                   );
                               })}
                            </CollapsibleSection>
                        );
                    })}
                </div>
            );
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center p-4 backdrop-blur-sm">
            <div className="bg-1 w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col animate-fade-in-up">
                {/* Header */}
                 <div className="flex justify-between items-start p-5 border-b border-line">
                    <div className="flex items-center gap-4">
                        <img src={user.picture} alt={user.name} className="h-14 w-14 rounded-full" />
                        <div>
                            <h2 className="text-xl font-bold text-p-text">{user.name}</h2>
                            <p className="text-sm text-s-text">{user.email}</p>
                            <div className="mt-2 text-2xl font-bold text-green-400">{formatCost(totals.grand)}
                                <span className="text-sm font-normal text-s-text ml-2">Total Estimated Cost</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-2"><IoClose className="h-5 w-5"/></button>
                </div>
                
                {/* Tabs */}
                <div className="p-3 border-b border-line">
                     <div className="flex space-x-2">
                        <button onClick={() => setActiveTab('tasks')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'tasks' ? 'bg-[#5652CF] text-white' : 'hover:bg-2 text-s-text'}`}>
                           <div className="flex items-center gap-2"><IoChatbubblesOutline /> Tasks <span className="text-xs opacity-80">({formatCost(totals.tasks)})</span></div>
                        </button>
                        <button onClick={() => setActiveTab('workflows')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'workflows' ? 'bg-[#5652CF] text-white' : 'hover:bg-2 text-s-text'}`}>
                            <div className="flex items-center gap-2"><IoBuildOutline /> Workflows <span className="text-xs opacity-80">({formatCost(totals.workflows)})</span></div>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 bg-body">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};